// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import DynamicListInput from '../../components/Form/DynamicListInput';
import TextInput from '../../components/Form/TextInput';
import { ClaimRequirementElement } from './Common';

interface RequiredClaimsInputProps {
  claimList: ClaimRequirementElement[];
  setClaimList: React.Dispatch<React.SetStateAction<ClaimRequirementElement[]>>;
}

const RequiredClaimsInput: React.FC<RequiredClaimsInputProps> = ({ claimList, setClaimList }) => {
  // Helper to update a specific claim within the list.
  const updateClaim = (claimId: string, key: 'property' | 'value', value: string) => {
    setClaimList((prev: ClaimRequirementElement[]) =>
      prev.map((claim) => (claim.id === claimId ? { ...claim, [key]: value } : claim)),
    );
  };

  return (
    <DynamicListInput<ClaimRequirementElement>
      list={claimList}
      setList={setClaimList}
      title="Claim Requirements"
      createNewItem={() => ({
        id: uuidv4(),
        property: '',
        value: '',
      })}
      renderItem={(claim: ClaimRequirementElement) => (
        <Box display="flex" gap={1} mb={0}>
          <TextInput
            label="Property"
            value={claim.property}
            setValue={(value) => updateClaim(claim.id, 'property', value as string)}
            required
          />
          <TextInput
            label="Value"
            value={claim.value}
            setValue={(value) => updateClaim(claim.id, 'value', value as string)}
            required
          />
        </Box>
      )}
    />
  );
};

export default RequiredClaimsInput;
